import {
  Menu,
  MenuList,
  MenuItem,
  Typography,
  Divider,
  ListItemText,
} from "@mui/material";
import React, { memo } from "react";

/**
 * RecoveryMenu Component - Handles row and column recovery operations
 * Extracted from main PdfTableViewer to improve maintainability
 */
const RecoveryMenu = ({
  // Menu state
  open,
  anchorEl,
  onClose,

  // Data
  deletedRows,
  deletedColumns,

  // Recovery functions
  recoverRowById,
  recoverColumnById,
  recoverAllRows,
  recoverAllColumns,
}) => {
  // Helper to parse original index from ID
  const getOriginalIndexFromId = (id, type) => {
    const parts = id.split("_");
    return parts.length >= 2 && parts[0] === type ? parseInt(parts[1], 10) : -1;
  };

  // Handle individual recovery with menu close
  const handleRecoverRow = (rowId) => {
    recoverRowById(rowId);
    onClose();
  };

  const handleRecoverColumn = (columnId) => {
    recoverColumnById(columnId);
    onClose();
  };

  // Handle bulk recovery with menu close
  const handleRecoverAllRows = () => {
    // Use the bulk recovery function that handles all rows in a single operation
    if (recoverAllRows) {
      recoverAllRows();
    }
    onClose();
  };

  const handleRecoverAllColumns = () => {
    // Use the bulk recovery function that handles all columns in a single operation
    if (recoverAllColumns) {
      recoverAllColumns();
    }
    onClose();
  };

  return (
    <Menu
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
    >
      <MenuList dense>
        <Typography
          variant={"subtitle2"}
          sx={{ px: 2, py: 1, fontWeight: "bold" }}
        >
          {"Recover Deleted Items"}
        </Typography>
        <Divider />

        {/* Deleted Rows Section */}
        {deletedRows.size > 0 && (
          <>
            <Typography
              variant={"caption"}
              sx={{ px: 2, py: 0.5, color: "text.secondary" }}
            >
              {"ROWS"}
            </Typography>
            {Array.from(deletedRows.entries())
              .sort((a, b) => {
                // Sort by originalIndex parsed from ID
                const getIndexFromId = (id) =>
                  getOriginalIndexFromId(id, "row");
                return getIndexFromId(a[0]) - getIndexFromId(b[0]);
              })
              .map(([rowId, rowInfo]) => {
                // Parse originalIndex from ID for display
                const originalIndexFromId = getOriginalIndexFromId(
                  rowId,
                  "row"
                );

                return (
                  <MenuItem
                    key={`row-${rowId}`}
                    onClick={() => handleRecoverRow(rowId)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      minWidth: "250px",
                      pl: 3,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#4caf50",
                        fontWeight: "bold",
                      }}
                    >
                      {"↺"}
                    </Typography>
                    <ListItemText
                      primary={`Row ${
                        originalIndexFromId >= 0
                          ? originalIndexFromId + 1
                          : "Unknown"
                      }`}
                      secondary={
                        rowInfo.content?.length > 0
                          ? `Content: ${
                              rowInfo.content.slice(0, 2).join(" | ") || "Empty"
                            }...`
                          : "No preview available"
                      }
                    />
                  </MenuItem>
                );
              })}
            <MenuItem
              onClick={handleRecoverAllRows}
              disabled={deletedRows.size === 0}
              sx={{ color: "success.main", pl: 3 }}
            >
              <Typography sx={{ fontSize: "14px", fontWeight: "bold", mr: 1 }}>
                {"↺↺"}
              </Typography>
              <ListItemText primary={"Recover All Rows"} />
            </MenuItem>
          </>
        )}

        {/* Deleted Columns Section */}
        {deletedColumns.size > 0 && (
          <>
            {deletedRows.size > 0 && <Divider />}
            <Typography
              variant={"caption"}
              sx={{ px: 2, py: 0.5, color: "text.secondary" }}
            >
              {"COLUMNS"}
            </Typography>
            {Array.from(deletedColumns.entries())
              .sort((a, b) => {
                // Sort by originalIndex parsed from ID
                const getIndexFromId = (id) =>
                  getOriginalIndexFromId(id, "col");
                return getIndexFromId(a[0]) - getIndexFromId(b[0]);
              })
              .map(([columnId, columnInfo]) => {
                // Parse originalIndex from ID for display
                const originalIndexFromId = getOriginalIndexFromId(
                  columnId,
                  "col"
                );

                return (
                  <MenuItem
                    key={`col-${columnId}`}
                    onClick={() => handleRecoverColumn(columnId)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      minWidth: "250px",
                      pl: 3,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#ff9800",
                        fontWeight: "bold",
                      }}
                    >
                      {"↺"}
                    </Typography>
                    <ListItemText
                      primary={
                        columnInfo.header ||
                        `Column ${
                          originalIndexFromId >= 0
                            ? originalIndexFromId + 1
                            : "Unknown"
                        }`
                      }
                      secondary={`Position ${
                        originalIndexFromId >= 0
                          ? originalIndexFromId + 1
                          : "Unknown"
                      } • ${
                        columnInfo.content?.filter(Boolean).length || 0
                      } cells`}
                    />
                  </MenuItem>
                );
              })}
            <MenuItem
              onClick={handleRecoverAllColumns}
              disabled={deletedColumns.size === 0}
              sx={{ color: "warning.main", pl: 3 }}
            >
              <Typography sx={{ fontSize: "14px", fontWeight: "bold", mr: 1 }}>
                {"↺↺"}
              </Typography>
              <ListItemText primary={"Recover All Columns"} />
            </MenuItem>
          </>
        )}

        {/* No items to recover */}
        {deletedRows.size === 0 && deletedColumns.size === 0 && (
          <MenuItem disabled>
            <ListItemText primary={"No deleted items to recover"} />
          </MenuItem>
        )}
      </MenuList>
    </Menu>
  );
};

export default memo(RecoveryMenu);
